<template lang="pug">
uc-preload-sfx(name="unit")
mixin dashboard-li
  li.relative.fade-in&attributes(attributes)
    .container
      button.ripple.group.p-3.pe-5.dark_text-gray-200.border-b.border-primary-400.text-left.rtl_text-right.transition-colors.flex.w-full.items-center.disabled_bg-transparent.disabled_opacity-50.dark_hover_text-white.focus-visible_focus-ring-8(
        :class="[{ 'saturate-0': isEditModeActive && isHidden(unit.id) }, { hidden: !isEditModeActive && isHidden(unit.id) }, 'hover_bg-primary-400/20 dark_hover_bg-primary-200/20 active_bg-primary-400/30 dark_active_bg-primary-200/30']"
        :disabled="locked(unit)"
        :to="unit.id"
        @click="$router.push(unit.id)"
        data-sfx="unit"
        tag="button"
      )
        uc-text.prose-stack.text-lg.me-12(
          :k="unit.id + '.title'"
          :text="removeLinebreaks(unit.title)"
        )
        span.ms-auto.transition-transform(
          :class="[!locked(unit) && 'group-hover_translate-x-1 -group-hover-rtl_translate-x-1', isEditModeActive && 'invisible']"
        )
          uc-icon.text-xl.ms-auto.text-primary-500(
            icon="theme:lock"
            v-if="locked(unit)"
          )
          uc-icon.text-xl.ms-auto.text-primary-600.dark_text-primary-400.dark_group-hover_text-white.group-hover_text-primary-500(
            icon="theme:check"
            v-else-if="isUnitCompleted(unit.id)"
          )
          uc-icon.text-xl.ms-auto.text-primary-600.dark_text-primary-400.rtl_-scale-x-100.dark_group-hover_text-white.group-hover_text-primary-500(
            icon="theme:right"
            v-else=""
          )

      //- edit mode include unit toggle (non-cards)
      uc-toggle-included(
        :class="{ dark: screen.dark }"
        :id="unit.id"
        :screen="screen"
        :unit="unit"
        type="unit"
        v-if="isEditModeActive && canToggleUnit(unit)"
      )

mixin hotspot-title-button
  button.container.flex.flex-col.items-center.p-5.dark_text-gray-200.dark_hover_text-white.text-white.focus-visible_focus-ring-8(
    data-sfx="click2"
    uc-word-skip=""
    @click="onTitleClick"
    :disabled="locked(unit)"
    :aria-label="hasTranslation(unit.title) + ', ' + (isUnitCompleted(unit.id) ? hasTranslation('Completed') : locked(unit) ? hasTranslation('Locked') : hasTranslation('Incomplete'))"
  )
    uc-icon.mt-1(
      v-if="locked(unit)"
      icon="theme:lock"
    )
    //- expose these strings for the translate script
    //- text="Completed"
    .hotspot-title-icons.flex.items-center.justify-center.auto-alpha-1.rounded-full.aspect-square.w-6.bg-secondary-500(
      v-else-if="isUnitCompleted(unit.id)"
    )
      uc-text.sr-only(:text="hasTranslation('Completed')" v-if="!(wordDoc || audioDoc)")
      uc-icon(
        icon="theme:check"
      )
    uc-text.prose-stack.text-2xl.mb-2.py-1.px-2.font-bold.rounded.shadow-md.dark_shadow-none.fancy-hover.ripple(
      :k="unit.id + '.title'"
      :text="unit.title"
      :class="twMerge('bg-primary-800 hover_bg-primary-700', locked(unit) && 'opacity-50', screen.styles?.['db-unit-title'])"
    )
    uc-text.prose-stack.text-lg.w-fit.px-2.rounded.shadow-md(
      :class="twMerge('text-primary-900 dark_text-primary-100 bg-primary-200 dark_bg-primary-800', locked(unit) && 'opacity-50', screen.styles?.['db-unit'])"
      :data-unit-number="screen.id"
      :k="unit && unit.number && 'Unit ${unitNumber}'"
      :text="unit && unit.number && '${unitLabel} ${unitNumber}'"
      :values="{ unitNumber: unit.number, unitLabel: unitLabel }"
      type="h3"
      uc-dont-read=""
      v-if="unit && unit.number && !hideUnit"
    )

//- dashboard titles/descriptions for word/audio docs
.word-audio-titles(v-if="wordDoc || audioDoc")
  ul
    li(v-for="unit in computedUnits")
      audio-marker(:name="`dashboard-hotspot-${unit.id}`" v-if="audioDoc")
      p
        uc-text.font-bold(
          :k="unit.id + '.title'"
          :text="removeLinebreaks(unit.title)"
        )
        template(v-if="unit.tag && wordDoc && !audioDoc")
          span &nbsp;
          uc-text(
            text="[ Dynamic content ]"
            uc-word-italic=""
          )
      uc-text(
        :k="unit.id + '.description'"
        :text="unit.description"
        type="p"
        v-if="unit.description"
      )

//- Table of Contents Layout
.screen-dashboard.text-center(
  uc-fade=""
  uc-word-skip=""
  v-if="!screen.cards && !isHotspotLayout && !isVideoLayout && !audioDoc"
)
  ul.mx-auto.max-w-prose.inline-flex.flex-col(v-if="!isTwoColumnLayout()")
    template(v-for="unit in computedUnits") 
      +dashboard-li 

  .two-column-wrapper.grid.grid-cols-2.gap-36(v-if="isTwoColumnLayout()")
    ul.mx-auto.max-w-full.inline-flex.flex-col
      template(v-for="(unit, index) in computedUnits") 
        +dashboard-li.w-full(v-if="index < computedUnits.length / 2")

    ul.mx-auto.max-w-full.inline-flex.flex-col
      template(v-for="(unit, index) in computedUnits") 
        +dashboard-li.w-full(v-if="index >= computedUnits.length / 2")

  //- Cards
.screen-dashboard.w-full.screen-dashboard--cards(
  uc-fade=""
  uc-word-skip=""
  v-else-if="!audioDoc && !isHotspotLayout && !isVideoLayout"
  role="navigation"
)
  ul.mx-auto.grid.grid-cols-1.gap-5(
    :class="[dashboardColsClass]"
  )
    li.relative.w-full.enter-grow(
      :class="[{ hidden: !isEditModeActive && isHidden(unit.id) }]"
      v-for="unit in computedUnits"
    )
      .container.h-full
        button.ripple.group.w-full.h-full.dark_text-gray-200.text-left.rtl_text-right.transition-colors.dark_hover_text-white.rounded.shadow-md.text-white.flex.flex-col.focus-visible_focus-ring-8(
          :class="[{ 'saturate-0 opacity-80': locked(unit) || (isEditModeActive && isHidden(unit.id)) }]"
          :disabled="locked(unit)"
          :to="unit.id"
          @click="$router.push(unit.id)"
          data-sfx="unit"
          tag="button"
        )
          .unit-img-label-wrapper.h-40.w-full.flex.relative.overflow-hidden
            span.fill.bg-cover.bg-no-repeat.bg-center.-z-1.duration-500.transition-transform.group-hover_duration-1000(
              :class="{ 'group-hover_scale-110': !locked(unit) && !suspend.reducedMotion }"
              :style="{ backgroundImage: unit['dashboard-img'] ? `url(${expandUploadPath(unit['dashboard-img'])})` : `url(${expandUploadPath(unit.bg)})` }"
            )
            span.fill.-z-1(
              :class="twMerge('from-primary-500/40 via-primary-700/60 to-primary-700 bg-gradient-to-bl', screen.styles?.['db-card'])"
              v-if="!screen['hide-card-tint']"
            )
            .unit-label-wrapper.self-end.w-full.py-2.pe-4.ps-6.flex.flex-row(
              :class="screen.banner ? twMerge('bg-primary-700 dark_bg-primary-900', screen.styles?.['db-banner']) : ''"
            )
              uc-text.text-xl.self-end.max-w-xs.pe-4.flex-1.font-bold(
                :class="[screen.banner ? 'text-white dark_text-primary-100' : 'text-white']"
                :k="unit.id + '.title'"
                :text="removeLinebreaks(unit.title)"
                v-if="screen.banner"
              ) 
              uc-icon.text-3xl.text-primary-800.dark_text-primary-200.ms-auto.self-end(
                icon="theme:lock"
                v-if="locked(unit)"
              )

              uc-icon.text-primary-100.dark_text-primary-100.text-3xl.ms-auto.self-end(
                icon="theme:check"
                v-else-if="isUnitCompleted(unit.id)"
              )

              span.inline-flex.flex-none.items-center.justify-center.h-8.w-8.rounded-full.text-lg.ms-auto.self-end.transition-colors(
                :class="screen.banner ? 'text-primary-200 bg-primary-800 group-hover_bg-primary-900 dark_text-primary-100 dark_bg-primary-600 dark_group-hover_bg-primary-700' : 'text-primary-800 bg-primary-100 group-hover_bg-primary-400'"
                v-else=""
              )
                uc-icon.rtl_-scale-x-100(icon="theme:right")

          .description-duration-wrapper.py-4.px-6.flex-1.flex.flex-col.gap-2.w-full(
            :class="twMerge('text-primary-900 dark_text-primary-100 bg-primary-200 dark_bg-primary-800', screen.styles?.['db-panel'])"
            v-if="!screen.banner || unit['description'] || unit['duration']"
          )
            .text-center.flex.flex-row.gap-1.items-center.opacity-80(
              v-if="unit['duration']"
            )
              uc-icon(icon="mdi:timer-outline") 
              uc-text(
                :values="{ n: unit['duration'] }"
                text="${n} minutes"
                type="p"
              )

            uc-text.text-xl.pe-4.font-bold(
              :k="unit.id + '.title'"
              :text="removeLinebreaks(unit.title)"
              type="h2"
              v-if="!screen.banner"
            )

            uc-text(
              :k="unit.id + '.description'"
              :text="unit['description']"
              v-if="unit['description']"
            )

          //- apply bg colour when no desc/duration to prevent transparent areas in case some units don't have them
          .w-full.flex-1(
            :class="twMerge('text-primary-900 dark_text-primary-100 bg-primary-200 dark_bg-primary-800', screen.styles?.['db-panel'])"
            v-else=""
          )

        //- edit mode include unit toggle (cards)
        uc-toggle-included(
          :class="{ dark: screen.dark }"
          :id="unit.id"
          :screen="screen"
          :unit="unit"
          type="unit"
          v-if="isEditModeActive && canToggleUnit(unit)"
        )

//- Hotspot Layout
.screen-dashboard.w-full.screen-dashboard--hotspot.fade-in(
  uc-fade=""
  uc-word-skip=""
  v-else-if="!audioDoc && isHotspotLayout"
  role="navigation"
  :aria-label="String(hasTranslation('Course navigation'))"
)
  uc-preload-sfx(name="complete")
  uc-preload-sfx(name="click2")
  span.uc-hotspot.relative.z-0.w-full.block(
    :class="$attrs.class"
    :data-src="screen.img"
    data-sfx="click2"
    ref="elRef"
    v-html="svg"
  )
  .dashboard__underlay.fixed.inset-0.h-screen.w-screen.auto-alpha-1.bg-gray-800.bg-opacity-70.z-0(
    v-if="activeDrawer"
    @click="closeDrawer"
  )
  .hotspot-titles(
    v-for="unit in computedHotspotUnits"
    :key="unit.id"
  )
    .hotspot-title.flex(
      :data-hotspot-id="unit.id"
      :class="{ 'is-label-on-hover': labelOnHover }"
      :ref="setHotspotRef"
      v-if="isUnitInHotspotSVG(unit.id)"
    )
      +hotspot-title-button

  .hotspot-drawer(
    v-for="unit in computedHotspotUnits"
    :key="unit.id"
    ref="drawerRef"
  )
    .unit-drawer.z-100.fixed.top-0.right-0.rtl_right-auto.rtl_left-0.h-full.w-96(
      :class="twMerge('min-w-[35rem]', activeDrawer === unit.id ? 'translate-x-0 rtl_translate-x-0 auto-alpha-1 visible bg-secondary-900 dark_bg-secondary-800 bg-opacity-90 dark_bg-opacity-90' : 'auto-alpha-0 translate-x-full rtl_-translate-x-full invisible', screen.styles?.['db-drawer'])"
      v-if="isUnitInHotspotSVG(unit.id)"
    )
      UseFocusTrap.flex.flex-col.justify-between.h-full(v-if="activeDrawer === unit.id")
        .drawer-header.flex.justify-end.aspect-video.min-h-8.bg-cover.bg-no-repeat.bg-center(
          :style="{ backgroundImage: unit['dashboard-img'] ? `url(${expandUploadPath(unit['dashboard-img'])})` : `url(${expandUploadPath(unit.bg)})` }"
        )
          uc-button.btn.btn-icon.cursor-auto.hover_bg-secondary-900.font-bold.w-8.h-8.m-4.z-100.text-primary-100.dark_text-primary-100.rounded-full.focus-visible_ring-8.bg-secondary-600(
            @click="closeDrawer"
            type="button"
            :aria-label="hasTranslation('Close')"
          )
            uc-icon(icon="theme:menu-close")
        .drawer-content.justify-start.grow.px-9.py-6
          .drawer-duration-wrapper.text-left.py-4.px-6.flex-1.flex.flex-col.gap-2(
            :class="twMerge('text-primary-100 dark_text-primary-100')"
          )
            uc-text.prose-stack.flex.text-lg.text-primary-400.dark_text-primary-400(
              :data-unit-number="screen.id"
              :k="unit && unit.number && 'Unit ${unitNumber}'"
              :text="unit && unit.number && '${unitLabel} ${unitNumber}'"
              :values="{ unitNumber: unit.number, unitLabel: unitLabel }"
              uc-dont-read=""
              v-if="unit && unit.number && !hideUnit"
            )
            uc-text.prose-stack.flex.text-2xl.pe-4.font-bold(
              :k="unit.id + '.title'"
              :text="unit.title"
              :class="[screen.styles?.title]"
            )
            .flex.gap-1.mb-4.opacity-80(
              v-if="unit['duration']"
            )
              uc-icon.mt-1(icon="mdi:timer-outline") 
              uc-text.prose-stack(
                :values="{ n: unit['duration'] }"
                text="${n} minutes"
                type="p"
              )
            uc-text.prose-stack(
              :k="unit.id + '.description'"
              :text="unit['description']"
              v-if="unit['description']"
            )
        .drawer-bottom.my-2
          .drawer-locked.flex.flex-row.justify-center.text-center.text-primary-100.dark_text-primary-100(
            v-if="locked(unit)"
          )
            uc-icon.mt-1.me-1(
              icon="theme:lock"
            )
            uc-text.prose-stack(
              :k="unit.id + '.title'"
              text="Unit is locked"
              type="p"
            )
          uc-button.btn.btn-primary.ripple.mt-2.mb-12.mx-auto(
            @click="$router.push(unit.id)"
            data-sfx="complete"
            :data-unit-id="unit.id"
            :disabled="locked(unit)"
          )
            uc-text(text="Begin unit")

//- Video Layout
.screen-dashboard.screen-dashboard--video.min-h-uc-screen.fade-in(
  uc-fade=""
  uc-word-skip=""
  v-else-if="!audioDoc && !wordDoc && isVideoLayout"
  role="navigation"
)
  uc-preload-sfx(name="complete")
  uc-preload-sfx(name="click2")
  .video-screen-container.overflow-hidden.fill.flex.flex-col.justify-center.items-center
    .video-container(
      :class="[isScaledVideo ? 'fill' : 'w-full']"
    )
      .dashboard-video-container.w-full.overflow-hidden(
        ref="elRefVideoLayout"
        :class="[isScaledVideo ? 'fill' : 'w-full']"
      )
        .dashboard-video.aspect-video.relative
          .hotspot-title.absolute(
            v-for="unit in computedVideoUnits"
            :data-hotspot-id="unit.page"
            )
              .title.flex(
                :ref="setHotspotRef"
              )
                button.container.flex.flex-col.items-center.p-5.dark_text-gray-200.dark_hover_text-white.text-white.focus-visible_ring(
                  data-sfx="click2"
                  uc-word-skip=""
                  @click="onTitleClick"
                  :disabled="locked(unit)"
                )
                  .title-container.flex.items-center.justify-center
                    .title-icon-container.z-0.absolute.auto-alpha-1.rounded-full(
                      :class="[{ 'next-unit': computedNextUnit === unit }, { 'completed': isUnitCompleted(unit.page) }, { 'locked': locked(unit) }, 'h-[48px] w-[48px] p-[0.125rem]']"
                      :data-test="locked(unit)"
                    )
                      uc-icon.text-4xl(
                        icon="theme:check"
                        v-if="isUnitCompleted(unit.page)"
                      )
                      uc-icon.text-2xl(
                        icon="theme:lock"
                        v-if="locked(unit)"
                      )
                    .title-text.z-100.auto-alpha-0
                      uc-text.prose-stack.text-2xl.font-bold(
                        :k="unit.id + '.title'"
                        :text="unit.title"
                        type="span"
                      )

                    uc-text.sr-only(:text="hasTranslation(unit.title)" v-if="!(wordDoc || audioDoc)")
                    uc-text.sr-only(:text="hasTranslation(getAriaLabel(unit.id))" v-if="!(wordDoc || audioDoc)")
          // - TODO: fallback for apple devices
          video.video-js.-z-1(
            crossOrigin="anonymous"
            playsInline="true"
            :loop="isVideoLoop"
            ref="elRefVideo"
            v-if="!showVideoPoster"
          )
          uc-asset.-z-1(
            :src="videoPosterImg"
            :class="twMerge('absolute inset-0 w-full h-full')"
            v-if="showVideoPoster"
          )

  .video-unit-titles(
    :class="{ 'hidden': hasAnimatedTitles }"
    v-for="unit in computedVideoUnits"
  )
    .hotspot-title.flex(
      :data-hotspot-id="unit.page"
      :ref="setVideoTitleRef"
    )
      +hotspot-title-button


</template>

<script lang="ts" setup>
import {
  inject,
  onMounted,
  computed,
  ref,
  unref,
  watchEffect,
  watch,
  nextTick,
  reactive,
  onBeforeUnmount
} from 'vue';
import { storeToRefs } from 'pinia';
import {
  breakpointsTailwind,
  useBreakpoints,
  onKeyDown,
  useEventListener,
  onClickOutside
} from '@vueuse/core';
import type { MaybeComputedRef } from '@vueuse/core';
import { throttle } from 'lodash-es';
import { UseFocusTrap } from '@vueuse/integrations/useFocusTrap/component';
import { twMerge } from '../lib/mergeStyles';
import expandUploadPath from '../lib/expandUploadPath';
import useAudio from '../stores/useAudio';
import useContent from '../stores/useContent';
import useTranslations from '../stores/useTranslations';
import useSuspend from '../lib/useSuspend';
import isHidden from '../lib/isHidden';
import useThemedSvg from '../lib/useThemedSvg';
import { createPopper } from '@popperjs/core';
import getRouter from '../lib/router';
import useFilterTags from '../lib/useFilterTags';
import useEditMode from '../stores/useEditMode';
import bugAlert from '../lib/bugAlert';
import { play as playAudio, stop as stopAudio } from '../lib/audio';
import useAudioNarration from '../lib/useAudioNarration';

const { available: mode } = storeToRefs(useEditMode());
const isEditMode = computed(() => mode.value === 'edit');

const { compiled } = storeToRefs(useContent());
import { setCompleted } from '../lib/SCORM';
const { getAudioURL } = useAudio();
const { resolveText, resolveNarrationAudio, audioAiJitEnabled } =
  useAudioNarration();

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');
const largerThanSm = breakpoints.greater('sm');
const largerThanLg = breakpoints.greater('lg');
const drawerRef = ref<HTMLElement | null>(null);
const currentSection = ref<HTMLElement | null>(null);

const content = compiled;
const unitsAndTest = inject('unitsAndTest');
const units = inject('units');
const finalUnlocked = inject('finalUnlocked') as Ref<boolean>;
const suspend = useSuspend();
const unlockUnits = inject('unlockUnits') as Ref<boolean>;
const unlockCourse = inject('unlockCourse') as Ref<boolean>;
const audioDoc = inject('audioDoc');
const wordDoc = unref<boolean>(inject('wordDoc'));
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const sectionId = inject('sectionId') as string;

const { translations } = useTranslations();
let initializedHotspot = false;
// bumped on `tagschanged` (adaptive content updated) so initSVG re-runs against the new unit list
const initVersion = ref(0);
const props = defineProps<{
  screen: UcScreen;
  editPath: string;
}>();

const filterHidden = items => {
  return items.filter(item => !isHidden(item.id));
};

const filterTags = useFilterTags();

// strip lines breaks and forced line break backslashes from the course title when used in the nav menu/bookmarks
const removeLinebreaks = (text: string) => {
  return text
    .replace(/ *\\?\n/g, ' ')
    .replace(/{r}/g, ' ')
    .replace(/{n}/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ');
};

const computedUnits = computed(() => {
  return props.screen.subunits ? filteredSubunits.value : unitsAndTest.value;
});

// TODO - add the placement asign to computedUnits
const computedHotspotUnits = computed(() => {
  const hotUnits = computedUnits.value.map(unit => {
    if (
      props.screen.targets &&
      props.screen.targets.some(target => target.page === unit.id)
    ) {
      let target = props.screen.targets.find(target => target.page === unit.id);
      return { ...unit, 'title-placement': target['title-placement'] };
    }
    return unit;
  });

  return hotUnits;
});

const computedVideoUnits = computed(() => {
  return props.screen.targets.map(target => {
    if (computedUnits.value.some(unit => unit.id === target.page)) {
      const unit = computedUnits.value.find(unit => unit.id === target.page);
      return {
        ...target,
        title: unit.title,
        description: unit.description,
        duration: unit.duration,
        type: unit.type,
        id: unit.id
      };
    }
  });
});

const computedNextUnit = computed(() => {
  return computedVideoUnits.value.find(
    unit => !isUnitCompleted(unit.id) && !locked(unit)
  );
});

const filteredSubunits = computed(() => {
  const listedSubunits = content.value.pages.filter(unit =>
    props.screen.subunits?.includes(unit.id)
  );
  const filterTaggedSubunits = filterTags(listedSubunits);
  const filterHiddenSubunits = filterHidden(filterTaggedSubunits);

  return (isEditMode.value ? filterTaggedSubunits : filterHiddenSubunits).map(
    (u, i) => ({ ...u, number: i + 1 })
  );
});

const areFilteredSubunitsCompleted = computed(() => {
  return filteredSubunits.value.every(unit => isUnitCompleted(unit.id));
});

const firstDashboardCols = computed(() => {
  return content.value.pages[0].screens.find(
    screen => screen.template === 'dashboard'
  )?.cols;
});

const dashboardColsClass = computed(() => {
  let gridColsClass;
  const cols = props.screen.cols || firstDashboardCols.value;
  switch (cols) {
    case 3:
      gridColsClass = 'md_grid-cols-4';
      break;
    case 4:
      gridColsClass = 'md_grid-cols-3';
      break;
    case 6:
      gridColsClass = 'md_grid-cols-2';
      break;
    case 12:
      gridColsClass = 'md_grid-cols-1';
      break;
    default:
      gridColsClass = 'md_grid-cols-3';
  }
  if (props.screen['dashboard-layout'] === 'masonry' && largerThanLg.value) {
    const masonryClass =
      cols === 3 || cols === 6 ? 'masonry-even-cols' : 'masonry-odd-cols';
    return `${gridColsClass} ${masonryClass}`;
  }
  return gridColsClass;
});

const isDefaultLayout = computed(() => {
  const thing =
    !largerThanSm.value ||
    props.screen['dashboard-layout'] === 'masonry' ||
    !isVideoLayout.value ||
    !props.screen['dashboard-layout'];
  return thing;
});

// - TODO: on small screens could display main SVG in background and have mobile sized/aspect ratio SVGs for each unit. Small portions of the main SVG.
const isHotspotLayout = computed(() => {
  return largerThanSm.value && props.screen['dashboard-layout'] === 'hotspot';
});

const isVideoLayout = computed(() => {
  return largerThanSm.value && props.screen['dashboard-layout'] === 'video';
});

const isScaledVideo = computed(() => {
  return typeof props.screen['scale-video'] === 'boolean'
    ? props.screen['scale-video']
    : false;
});

let autoPlayVideoCount = ref(suspend.completed[props.screen.id] ? 1 : 0);

const canAutoPlayVideo = computed(() => {
  let autoPlay = true;
  const autoPlayLimit = props.screen['autoplay-video'];

  if (suspend.reducedMotion) {
    return false;
  }
  if (autoPlayLimit) {
    autoPlay =
      autoPlayLimit === 'always' || autoPlayLimit === 'true'
        ? true
        : autoPlayLimit === 'never' || autoPlayLimit === 'false'
          ? false
          : autoPlayLimit === 'once'
            ? autoPlayVideoCount.value < 1
            : false;
  }
  return autoPlay;
});

const hasAnimatedTitles = computed(() => {
  return !suspend.reducedMotion && !props.screen['show-titles'];
});

const isVideoLoop = computed(() => {
  if (!canAutoPlayVideo.value) return false;
  const autoplay = props.screen['autoplay-video'];
  return autoplay === 'false' || autoplay === 'never' || autoplay === 'once'
    ? false
    : true;
});

const videoPosterImg = computed(() => {
  if (!props.screen['video-poster'])
    bugAlert(
      'You have set a video dashboard but have not set a fallback image, please add a `video-poster` prop to the screen: ' +
        props.screen.id
    );
  return props.screen['video-poster'];
});

const unitLabel = computed(() => {
  return content.value.course['unit-label'] || 'Unit';
});

const hideUnit = computed(() => {
  return content.value.course['hide-unit-labels']
    ? true
    : props.screen['hide-unit']
      ? true
      : false;
});

const labelOnHover = computed(() => !!props.screen['label-on-hover']);

const currentSectionId = computed(() => {
  return currentSection.value?.getAttribute('id');
});

const isDrawerHidden = function (unit) {
  // TODO - show drawer for video dashboard
  if (isVideoLayout.value) return true;
  const hasDescOrDuration = unit['description'] || unit['duration'];
  const screenHideDrawer = props.screen['hide-drawer'];
  const hideDrawer = screenHideDrawer
    ? true
    : screenHideDrawer === false
      ? false
      : !hasDescOrDuration
        ? true
        : false;
  return hideDrawer;
};

const isTwoColumnLayout = function () {
  return (
    largerThanSm.value &&
    (computedUnits?.value?.length > 8 || props.screen.split)
  );
};

const isUnitCompleted = function (unit) {
  return suspend.completed[unit];
};

const canToggleUnit = function (unit) {
  if (unit?.type === 'final') return false;

  // don't allow excluding the last visible unit
  const visibleUnits = units?.value.filter(unit => !isHidden(unit.id));

  return isHidden(unit.id) || visibleUnits.length > 1;
};

const locked = function (page) {
  const { linear } = content.value.course;
  const { completed } = suspend;

  if (unlockCourse.value) {
    return false;
  }

  if (page.type === 'final') {
    return !finalUnlocked.value;
  }

  if (unlockUnits.value) {
    return false;
  }

  // unlock all units when the course isn't linear
  if (!linear) {
    return false;
  }

  let unlocked = true;
  let i = units.value.findIndex(u => u.id === page.id);

  while (i >= 0 && i-- && unlocked) {
    const prevId = units.value[i].id;

    if (!completed[prevId]) {
      unlocked = false;
    }
  }

  return !unlocked;
};

function hasTranslation(key: string | number, fallback: string | number = key) {
  // get the translation if it exists
  let maybeTranslated =
    translations?.[suspend.locale]?.[key] ||
    translations?.[suspend.locale]?.[fallback];
  return maybeTranslated || key;
}

/**
 * dashboard-hotspot
 * SVG target class name must match the unit id
 */

const activeDrawer = ref<string | null>(null);
const hotspotEls = reactive({});
const unitsInSVG = ref([]);
const videoTitleEls = reactive({});
const popperInstanceCache: { [id: string]: Instance } = {};

const setHotspotRef = (el: HTMLElement) => {
  if (!el) return;
  const id = el?.dataset?.hotspotId;
  if (id) hotspotEls[id] = el;
  return el;
};

const showHotspotTitles = () => {
  const rootBoundry = document.querySelector(
    `#${sectionId} .uc-hotspot`
  ) as HTMLElement;

  computedHotspotUnits?.value.forEach(unit => {
    if (!hotspotEls[unit.id]) return;
    const target = document.querySelector(
      `#${sectionId} [data-class=targets] > [data-class="${unit.id}"]`
    ) as HTMLElement;
    const boundary = hotspotEls[unit.id] as HTMLElement;

    popperInstanceCache[unit.id] = createPopper(target, hotspotEls[unit.id], {
      placement: unit['title-placement'] || 'top',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            mainAxis: false,
            boundary: boundary,
            rootBoundary: rootBoundry
          }
        }
      ]
    });
  });
};

const onTitleClick = (e: MouseEvent) => {
  e.preventDefault();
  stopAudio();
  const el = e.target as HTMLElement;
  const id = el.closest('.hotspot-title')?.dataset.hotspotId;

  if (locked(content.value.pages.find(unit => unit.id === id))) return;

  if (id) {
    const unit = computedUnits.value.find(unit => unit.id === id);
    if (isDrawerHidden(unit)) {
      getRouter().push(`/${unit.id}`);
      return;
    }

    openDrawer(unit, id);
  }
};

const onHotspotClick = async (e: MouseEvent | KeyboardEvent) => {
  e.preventDefault();
  stopAudio();
  if (!isHotspotLayout.value) {
    return;
  }
  const el = e.target as SVGElement;
  const target = el.closest('[data-class=targets] > g') as SVGGElement;

  if (target) {
    const name = target.dataset.class;

    if (!name) return;
    if (locked(content.value.pages.find(unit => unit.id === name))) return;
    if (isNameNotInUnits(name)) return;
    const unit = computedUnits.value.find(unit => unit.id === name);
    if (isDrawerHidden(unit)) {
      // open the unit
      getRouter().push(`/${unit.id}`);
      return;
    }

    openDrawer(unit, name);
  }
};

const openDrawer = async function (unit, drawerId) {
  if (activeDrawer.value === drawerId) return;
  activeDrawer.value = drawerId;

  // Audio key matches the `dashboard-hotspot-${unit.id}` audio-marker. JiT
  // synthesizes the drawer's narration (title + description); S3 plays the
  // pre-recorded file. JiT/S3 are mutually exclusive, so branch explicitly —
  // getAudioURL returns null when JiT is enabled.
  const key = `dashboard-hotspot-${unit.id}`;
  let audioFile: string | null | undefined;

  if (audioAiJitEnabled.value) {
    const text = [
      unit.title ? resolveText(`${unit.id}.title`, unit.title) : '',
      unit.description
        ? resolveText(`${unit.id}.description`, unit.description)
        : ''
    ]
      .filter(Boolean)
      .join('. ');
    audioFile =
      text && props.screen.id
        ? await resolveNarrationAudio({ screenId: props.screen.id, key, text })
        : null;
  } else {
    audioFile = getAudioURL(key);
  }

  if (audioFile) playAudio(audioFile);
};

const closeDrawer = function () {
  stopAudio();
  activeDrawer.value = null;
};

const isNameNotInUnits = function (name: string) {
  return !computedUnits?.value?.some(unit => unit.id === name);
};

const isUnitInHotspotSVG = function (id) {
  return unitsInSVG?.value?.some(unit => unit === id);
};

const svg = useThemedSvg(
  { src: props.screen.hotspot as string },
  { transformClass: true }
);

const onEscapePressed = function () {
  if (isHotspotLayout.value && activeDrawer.value) {
    closeDrawer();
  }
};

const getAriaLabel = (name: string) => {
  const unit = computedUnits.value.filter(unit => unit.id === name)[0];

  if (!unit) return '';

  const targetState = isUnitCompleted(name) ? 'completed' : 'incomplete';
  const targetText = unit.title || unit.heading || unit.subheading || '';

  return `${targetText} ${targetState}`;
};

const elRef = ref<HTMLSpanElement>();
useEventListener(elRef, 'click', onHotspotClick);
onKeyDown([' ', 'Enter'], onHotspotClick, {
  target: elRef as MaybeComputedRef<EventTarget>
});

onKeyDown('Escape', onEscapePressed);
onClickOutside(drawerRef, closeDrawer);

const elRefVideoLayout = ref<HTMLDivElement>();
const elRefVideo = ref<HTMLVideoElement>();
const videoTargetAnimValues = reactive({});
const videoTargetTimelines = reactive({});
const canApplyNextUnit = ref<Boolean>(true);
const playerCache: { [id: string]: VideoJs.Player } = {};
const targetsInitialised = ref<Boolean>(false);
const showVideoPoster = ref<Boolean>(false);

function getPlayer(): VideoJs.Player {
  return playerCache[props.screen.id];
}

function destroyPlayer(): void {
  if (playerCache[props.screen.id]) {
    playerCache?.[props.screen.id]?.dispose();
    delete playerCache[props.screen.id];
  }
}

const play = (player?: VideoJs.Player) => {
  if (player) showVideoPoster.value = false;
  player.play()?.catch(() => {
    nextTick(() => {
      showVideoPoster.value = true;
      console.error(
        `A video failed to play, probably because of the browser's autoplay policy. This is unlikely to be an issue for real users! \n\nLearn more: http://lms.vinciworks.com/awscourses_test/docs/author/video.html#autoplay`
      );
    });
  });
};

const setupVideoDashboard = async () => {
  const videoEl = unref(elRefVideo);
  if (!videoEl) return;

  const { videojs } = await import('../lib/videojs');
  const src = props.screen.video; // TODO: may need to have localised video

  const player: VideoJs.Player = videojs(
    videoEl,
    {
      controls: false,
      fill: true,
      nativeTextTracks: false,
      html5: { hls: { overrideNative: true } },
      sources: [
        { type: src.includes('.m3u8') ? 'application/x-mpegURL' : '', src }
      ]
    },

    function onPlayerReady() {
      this.muted(suspend.mute ?? suspend.videoMute ?? false);
      this.volume(1);
    }
  );

  await new Promise(r => player.ready(r as VideoJs.ReadyCallback));
  player.src(src.trim());
  player.on('canplay', () => {
    if (canAutoPlayVideo.value && player && player.paused()) {
      play(player);
    }
  });

  player.on('play', () => {
    showVideoPoster.value = false;
  });

  player.on('error', e => {
    // TODO deal with errors
    console.error('Dashboard video error', e);
    nextTick(() => {
      showVideoPoster.value = true;
    });
  });

  useEventListener(window, 'scrollsectionentered', async e => {
    currentSection.value = e.target as HTMLElement;

    if (currentSectionId.value === props.screen.id) {
      await setupVideoTargets();
      try {
        if (canAutoPlayVideo.value && player && player.paused()) play(player);
      } catch (error) {
        console.log('Error playing video', error);
      }
    }
  });

  playerCache[props.screen.id] = player;

  useEventListener(window, 'resize', throttledResize);
};

const setVideoTitleRef = (el: HTMLElement) => {
  if (!el) return;
  const id = el?.dataset?.hotspotId;
  if (id) videoTitleEls[id] = el;
  return el;
};

const setupVideoTargets = async () => {
  if (!isVideoLayout.value) return false;
  if (targetsInitialised.value) return true;

  if (isVideoLayout.value) {
    const videoEl = unref(elRefVideoLayout);
    if (!videoEl) return;

    computedVideoUnits.value.forEach(unit => {
      const target = videoEl.querySelector(
        `.dashboard-video [data-hotspot-id="${unit.page}"]`
      ) as HTMLElement;
      const targetIcon = target.querySelector(
        '.title-icon-container'
      ) as HTMLElement;
      if (!target) return;
      if (unit.top) target.style.top = `${unit.top}%`;
      if (unit.left) target.style.left = `${unit.left}%`;

      videoTargetTimelines[unit.page] = targetTitleAnim(target, unit.page);

      let isHovered = false;

      const onEnterTarget = () => {
        if (targetIcon && targetIcon.classList.contains('next-unit')) {
          targetIcon.classList.remove('next-unit');
        }
        if (hasAnimatedTitles.value && videoTargetTimelines[unit.page])
          videoTargetTimelines[unit.page].play();
      };

      const onLeaveTarget = () => {
        if (hasAnimatedTitles.value && videoTargetTimelines[unit.page])
          videoTargetTimelines[unit.page].reverse();
      };

      target.addEventListener('mouseenter', function () {
        isHovered = true;
        onEnterTarget();
      });

      target.addEventListener('focusin', function () {
        if (!isHovered) onEnterTarget();
      });

      target.addEventListener('mouseleave', function () {
        isHovered = false;
        onLeaveTarget();
        if (
          canApplyNextUnit.value &&
          targetIcon &&
          computedNextUnit.value &&
          computedNextUnit.value.id === unit.page
        ) {
          canApplyNextUnit.value = false;
          setTimeout(() => {
            canApplyNextUnit.value = true;
            targetIcon.classList.add('next-unit');
          }, 6000);
        }
      });

      target.addEventListener('focusout', function () {
        if (!isHovered) onLeaveTarget();
      });

      const rootBoundry = document.querySelector(
        `#${sectionId} .dashboard-video`
      ) as HTMLElement;
      const boundary = videoTitleEls[unit.page] as HTMLElement;
      const placement = unit['title-placement'] || 'top';

      popperInstanceCache[unit.page] = createPopper(
        target,
        videoTitleEls[unit.page],
        {
          placement: placement,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                mainAxis: false,
                boundary: boundary,
                rootBoundary: rootBoundry
              }
            }
          ]
        }
      );
    });
  }
  targetsInitialised.value = true;
  return true;
};

const targetTitleAnim = (target, id) => {
  const targetTimeLine = gsap.timeline({ paused: true });

  const newWidth = target.querySelector('.title-text').offsetWidth + 24;
  const titleHeight =
    target.querySelector('.title-text').offsetHeight > 0
      ? target.querySelector('.title-text').offsetHeight
      : 48;
  const iconHeight =
    target.querySelector('.title-icon-container').offsetHeight > 0
      ? target.querySelector('.title-icon-container').offsetHeight
      : 48;

  // final anim values
  const xTitleOffset = (newWidth / 2) * -1;
  const yTitleOffset =
    titleHeight > iconHeight ? (titleHeight - iconHeight) * -1 : 0;
  const newHeight = titleHeight + 24;

  videoTargetAnimValues[id] = {
    xTitleOffset,
    yTitleOffset,
    newHeight
  };

  // TODO - use tranform instead of margin for performance
  targetTimeLine
    .to(target.querySelector('.title-icon-container'), {
      duration: 0.4,
      width: newWidth,
      height: videoTargetAnimValues[id].newHeight,
      backgroundColor: '#facd2e',
      color: '#facd2e',
      ease: 'easeOut',
      'margin-left': videoTargetAnimValues[id].xTitleOffset,
      'transform-origin': '0 0',
      y: videoTargetAnimValues[id].yTitleOffset,
      onComplete: () => {
        targetTimeLine.to(target.querySelector('.title-text'), {
          duration: 0.4,
          autoAlpha: 1,
          ease: 'easeOut'
        });
      }
    })
    .set(target.querySelector('.title-icon-container'), {
      'z-index': 100
    });

  return targetTimeLine;
};

const resetTargetAnim = () => {
  const videoEl = unref(elRefVideoLayout);
  if (!videoEl) return;
  computedVideoUnits.value.forEach(unit => {
    const target = videoEl.querySelector(
      `.dashboard-video [data-hotspot-id="${unit.page}"]`
    ) as HTMLElement;
    if (!target) return;

    delete videoTargetAnimValues[unit.page];
    delete videoTargetTimelines[unit.page];
    videoTargetTimelines[unit.page] = targetTitleAnim(target, unit.page);
  });
};

const onResize = () => {
  if (hasAnimatedTitles.value) resetTargetAnim();

  if (!isScaledVideo.value) return;

  // resize the video so it fits the largest dimension of width or height
  const videoEl = unref(elRefVideoLayout);
  if (!videoEl) return;

  const video = videoEl.querySelector<HTMLElement>('video') || videoEl; // the video

  if (!video) return;

  video.style.transform = 'none';
  video.style.transformOrigin = '0 0';

  const { width: w1, height: h1 } = videoEl.getBoundingClientRect();
  const { width: w2, height: h2 } = video.getBoundingClientRect();

  let scale = Math.max(w1 / w2, h1 / h2);

  video.style.transform = `scale(${scale})`;
};

const throttledResize = throttle(onResize, 100, { leading: false });

onMounted(async () => {
  if (
    props.screen.template === 'subunit-dashboard' &&
    areFilteredSubunitsCompleted.value
  ) {
    setCompleted(props.screen.id);
  }

  // setup the video player
  if (isVideoLayout.value && props.screen.video) {
    await setupVideoDashboard();
  }
});

// when svg is ready:
// - apply a11y to targets
// - init parallax

// FIXME - add parralax
watchEffect(
  async function initSVG() {
    initVersion.value; // subscribe so tagschanged forces a re-run
    const el = unref(elRef);
    if (!el) return;
    //const targets = el.querySelectorAll('[data-class*="parallax-"]');
    if (initializedHotspot) return;
    if (!svg.value) return;
    if (!el) return;
    initializedHotspot = true;
    // TODO: show SVG targets and base if in edit mode
    if (!isEditModeActive.value) {
      el.querySelectorAll('[data-class=base] > g').forEach(
        (base: SVGGElement) => {
          base.style.display = 'none';
        }
      );
    }

    el.querySelectorAll('[data-class=targets] > g').forEach(
      (target: SVGGElement) => {
        const name = target.dataset.class;

        if (!name) return;

        if (isNameNotInUnits(name)) {
          target.style.display = 'none';
        } else {
          const base = el.querySelector(
            `[data-class=base] > [data-class="${name}"]`
          ) as HTMLElement;
          if (base && base.style) base.style.display = 'block';

          // on an earlier run this target's name may have been absent from computedUnits
          // and got display:none set above — clear it now that the unit is known
          target.style.display = '';

          // listener attachment / unitsInSVG push are not idempotent; guard against re-runs
          if (target.dataset.initialized !== 'true') {
            target.dataset.initialized = 'true';

            target.setAttribute('tabindex', '-1');
            target.setAttribute('aria-hidden', 'true');

            const onEnter = () => {
              window.requestAnimationFrame(() => {
                gsap.to(target, {
                  opacity: 1,
                  overwrite: true,
                  duration: 0.15,
                  ease: 'linear'
                });
              });
              if (labelOnHover.value && hotspotEls[name]) {
                hotspotEls[name].classList.add('is-visible');
              }
            };

            const onLeave = () => {
              gsap.to(target, { opacity: 0, overwrite: true, duration: 0.3 });
              if (labelOnHover.value && hotspotEls[name]) {
                hotspotEls[name].classList.remove('is-visible');
              }
            };

            target.addEventListener('mouseenter', onEnter);
            target.addEventListener('focus', onEnter);

            target.addEventListener('mouseleave', onLeave);
            target.addEventListener('blur', onLeave);
            unitsInSVG.value.push(name);
          }
        }
      }
    );

    if (isHotspotLayout.value) {
      nextTick(() => {
        showHotspotTitles();
      });
    }
  },
  { flush: 'post' }
);

watch(isHotspotLayout, (curr, prev) => {
  if (!curr) {
    initializedHotspot = false;
    closeDrawer();
  }
  if (curr && !prev) {
    showHotspotTitles();
    // nextTick(() => {
    //   // remounting into an already-active section won't re-fire ScrollTrigger's onToggle,
    //   // so manually restore .-in (the class fade-in uses to flip visibility/opacity on)
    //   elRef.value?.closest('.fade-in')?.classList.add('-in');
    //   const page = elRef.value?.closest('.page') as { refreshSections?: () => Promise<unknown> } | null;
    //   page?.refreshSections?.();
    // });
  }
});

watch(isEditModeActive, (curr, prev) => {
  if (isHotspotLayout.value) {
    nextTick(() => {
      initializedHotspot = false;
      closeDrawer();
      showHotspotTitles();
    });
  }
});

// re-init when the unit set changes — covers async initial content load and adaptive tag toggles.
// `tagschanged` would only catch the tag case; watching `computedUnits` directly catches both.
watch(computedUnits, () => {
  if (!isHotspotLayout.value) return;
  initializedHotspot = false;
  initVersion.value++;
});

// TODO - move elements out of setupVideoDashboard that can be run once onMounted
watch(largerThanSm, (curr, prev) => {
  if (isVideoLayout.value && curr) {
    nextTick(() => {
      setupVideoDashboard();
      setupVideoTargets();
    });
  }
});

watch(smallDevice, (curr, prev) => {
  if (isVideoLayout.value && curr) {
    targetsInitialised.value = false;
    destroyPlayer();
  }
});

watchEffect(() => {
  if (isVideoLayout.value && hasAnimatedTitles.value) {
    const player = getPlayer();
    if (player && player.paused() && !suspend.reducedMotion) {
      play(player);
    }
  }

  if (isVideoLayout.value && !hasAnimatedTitles.value) {
    getPlayer()?.pause();

    computedVideoUnits.value.forEach(unit => {
      if (popperInstanceCache[unit.page]) {
        popperInstanceCache[unit.page].update();
      }
    });
  }
});

watchEffect(() => {
  if (isHotspotLayout.value) {
    unitsInSVG.value.forEach(unit => {
      if (isNameNotInUnits(unit)) return;
      const completed = document.querySelector(
        `[data-class=completed] > [data-class="${unit}"]`
      ) as HTMLElement;
      if (isUnitCompleted(unit)) {
        if (completed) completed.style.opacity = '1';
      }
    });
  }
});

onBeforeUnmount(() => {
  try {
    destroyPlayer();
  } catch (e) {
    console.error(e);
  }
});
</script>
<style scoped>
.masonry-odd-cols {
  :nth-child(7n + 1) {
    grid-column: span 2;
    grid-row: span 1;
  }

  :nth-child(7n + 2) {
    grid-column: span 1;
    grid-row: span 2;
  }

  :nth-child(5n + 4) {
    grid-column: span 1;
    grid-row: span 2;
  }
}

.masonry-even-cols {
  :nth-child(7n + 4) {
    grid-column: span 1;
    grid-row: span 2;
  }

  :nth-child(7n + 5) {
    grid-column: span 2;
    grid-row: span 1;
  }

  :nth-child(7n + 9) {
    grid-column: span 2;
    grid-row: span 1;
  }
}

:deep(svg) {
  width: 100%;
  height: auto;
  top: 0;
  left: 0;
}

:deep([data-class='targets'] > g),
:deep([data-class='completed'] > g) {
  @apply opacity-0 transition-opacity;
}

:deep([data-class='targets'] > g) {
  @apply cursor-pointer !outline-none;

  &:focus-visible {
    outline: rgb(var(--color-sky-700)) solid 8px !important;
  }

  &:focus-visible:where(.dark *) {
    outline: rgb(var(--color-sky-400)) solid 8px !important;
  }
}

/* Video Dashboard  */

.title-icon-container {
  border: rgb(250, 205, 46);
  background-color: #ffff;
  border-style: solid;
  border-width: 12px;
  top: 50%;
  left: 50%;
  margin-top: -24px;
  transition: visibility 0.4s;
}

.title-icon-container.completed {
  border-color: #ffff;
  background-color: rgb(var(--color-green-900));
  border-width: 4px;
}

.title-icon-container.locked {
  border-color: #ffff;
  background-color: rgb(var(--color-gray-800) / 50%);
  border-width: 4px;
}

.hotspot-title:hover {
  .title-icon-container {
    border-color: #facd2e;

    .icon {
      visibility: hidden;
    }
  }
}

.title-text {
  color: #000;
  opacity: 0;
  @apply px-4;
  transition: opacity 1s;
}

.video-unit-titles .hotspot-title .hotspot-title-icons {
  @apply hidden;
}

.hotspot-title.is-label-on-hover {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-out;
}

.hotspot-title.is-label-on-hover.is-visible,
.hotspot-title.is-label-on-hover:focus-within {
  opacity: 1;
  pointer-events: auto;
}

.hotspot-title:not(:hover) {
  .next-unit {
    animation-delay: 2s;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(250, 205, 46, 0.7);
    }

    70% {
      transform: scale(1.2);
      box-shadow: 0 0 0 10px rgba(250, 205, 46, 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(250, 205, 46, 0);
    }
  }
}
</style>
